import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  rendicionCampanaService,
  TipoPresentacionRendicion,
} from '../../../services/api';
import type {
  CandidaturaCampana,
  EtapaEleccion,
  RendicionCampana,
  TipoEleccion,
} from './types';
import { LABEL_ETAPA, LABEL_TIPO_ELECCION } from './types';

// =============================================================================
// Schema
// =============================================================================

const schema = z
  .object({
    tipoPresentacion: z.enum(['COMPLETA', 'COPIA_DISTRITO']),
    tipoEleccion: z.enum([
      'LEGISLATIVA_PROVINCIAL',
      'EJECUTIVA_PROVINCIAL',
      'MUNICIPAL',
      'CONSEJO_MAGISTRATURA',
    ]),
    etapa: z.enum(['PRIMARIA', 'GENERAL', 'SEGUNDA_VUELTA']),
    fechaActoElectoral: z.string().min(1, 'Requerida'),
    codigoSpeca: z.string().optional(),
  })
  .refine(
    (d) => d.tipoPresentacion === 'COMPLETA' || (d.codigoSpeca && d.codigoSpeca.trim().length > 0),
    { message: 'Requerido para COPIA_DISTRITO', path: ['codigoSpeca'] },
  );

type FormData = z.infer<typeof schema>;

interface PartidoLite {
  id?: string;
  numero?: number;
  denominacion?: string;
  sigla?: string | null;
  tipo?: string;
  domicilioLegal?: string | null;
  localidad?: string | null;
  codigoPostal?: string | null;
}

interface Props {
  partidoId: string;
  partido: PartidoLite | null;
  rendicion: RendicionCampana | null;
  snapshotMode?: boolean;
  onCreated: (rendicion: RendicionCampana) => void;
  onNext: () => void;
  onChangeTipo: (tipo: TipoPresentacionRendicion) => void;
}

function tipoPresentacionSegunPartido(tipoPartido: string | undefined): TipoPresentacionRendicion | null {
  if (!tipoPartido) return null;
  if (tipoPartido === 'DISTRITO') return 'COPIA_DISTRITO';
  // Sin 'CONFEDERACION': dejó de ser un `TipoPartido`. Una confederación llega acá con
  // su ámbito real (la migración de alianzas las dejó en PROVINCIAL), así que sigue
  // cayendo en COMPLETA por esa vía.
  if (['MUNICIPAL', 'PROVINCIAL'].includes(tipoPartido)) return 'COMPLETA';
  return null;
}

function plazoVencimientoSugerido(fecha: string): string {
  if (!fecha) return '';
  const d = new Date(fecha);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + 60);
  return d.toLocaleDateString('es-AR');
}

// =============================================================================
// Componente
// =============================================================================

export default function Paso1Datos({
  partidoId,
  partido: partidoProp,
  rendicion,
  snapshotMode,
  onCreated,
  onNext,
  onChangeTipo,
}: Props) {
  const queryClient = useQueryClient();

  const partido = rendicion?.partido ?? partidoProp;
  const tipoSugerido = tipoPresentacionSegunPartido(partido?.tipo);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      tipoPresentacion: rendicion?.tipoPresentacion ?? tipoSugerido ?? 'COMPLETA',
      tipoEleccion: rendicion?.tipoEleccion ?? 'LEGISLATIVA_PROVINCIAL',
      etapa: rendicion?.etapa ?? 'GENERAL',
      fechaActoElectoral: rendicion?.fechaActoElectoral?.slice(0, 10) ?? '',
      codigoSpeca: rendicion?.codigoSpeca ?? '',
    },
  });

  useEffect(() => {
    if (!rendicion && tipoSugerido) {
      setValue('tipoPresentacion', tipoSugerido);
      onChangeTipo(tipoSugerido);
    }
  }, [rendicion, tipoSugerido, setValue, onChangeTipo]);

  const tipoActual = watch('tipoPresentacion');
  const fechaActo = watch('fechaActoElectoral');
  const esCopiaDistrito = tipoActual === 'COPIA_DISTRITO';

  useEffect(() => {
    onChangeTipo(tipoActual);
  }, [tipoActual, onChangeTipo]);

  const saveMutation = useMutation({
    mutationFn: async (data: FormData) => {
      onChangeTipo(data.tipoPresentacion);
      const payload = {
        tipoPresentacion: data.tipoPresentacion,
        tipoEleccion: data.tipoEleccion as TipoEleccion,
        etapa: data.etapa as EtapaEleccion,
        fechaActoElectoral: data.fechaActoElectoral,
        codigoSpeca: data.codigoSpeca || null,
        versionSpeca: null,
      };
      if (rendicion) {
        return rendicionCampanaService.updatePaso1(rendicion.id, payload);
      }
      return rendicionCampanaService.create(partidoId, payload);
    },
    onSuccess: (res) => {
      toast.success(rendicion ? 'Paso 1 actualizado' : 'Rendición creada');
      queryClient.invalidateQueries({ queryKey: ['rendiciones-campana-partido', partidoId] });
      onCreated(res.data as RendicionCampana);
      onNext();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? 'No se pudo guardar');
    },
  });

  const onSubmit = (data: FormData) => {
    if (rendicion && !isDirty) {
      onNext();
      return;
    }
    saveMutation.mutate(data);
  };

  const plazo = useMemo(() => plazoVencimientoSugerido(fechaActo), [fechaActo]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6 space-y-5">
        <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3">
          Datos del Acto Electoral
        </h2>

        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-sm text-blue-900">
          La rendición de campaña se presenta dentro de los <strong>60 días</strong> posteriores al
          acto electoral (Art. 53 inc. c, Ley XII-23). Los partidos de distrito que ya presentaron
          ante la CNE solo deben adjuntar copia.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Tipo de presentación
            </label>
            <select
              {...register('tipoPresentacion')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="COMPLETA">Carga completa (provincial / municipal)</option>
              <option value="COPIA_DISTRITO">Copia de presentación nacional (distrito)</option>
            </select>
            {tipoSugerido && tipoActual !== tipoSugerido && (
              <p className="mt-1 text-xs text-amber-700">
                Para partidos {partido?.tipo}, la presentación habitual es{' '}
                <strong>
                  {tipoSugerido === 'COPIA_DISTRITO'
                    ? 'Copia de presentación nacional'
                    : 'Carga completa'}
                </strong>
                .
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Agrupación política
            </label>
            <input
              type="text"
              readOnly
              value={partido ? `${partido.denominacion}${partido.sigla ? ` (${partido.sigla})` : ''}` : '— seleccionar —'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-600"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Tipo de elección
            </label>
            <select
              {...register('tipoEleccion')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {(Object.keys(LABEL_TIPO_ELECCION) as TipoEleccion[]).map((k) => (
                <option key={k} value={k}>{LABEL_TIPO_ELECCION[k]}</option>
              ))}
            </select>
            {errors.tipoEleccion && <p className="mt-1 text-xs text-red-600">{errors.tipoEleccion.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Etapa
            </label>
            <select
              {...register('etapa')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {(Object.keys(LABEL_ETAPA) as EtapaEleccion[]).map((k) => (
                <option key={k} value={k}>{LABEL_ETAPA[k]}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Fecha del acto electoral
            </label>
            <input
              type="date"
              {...register('fechaActoElectoral')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.fechaActoElectoral && <p className="mt-1 text-xs text-red-600">{errors.fechaActoElectoral.message}</p>}
          </div>
        </div>

        {plazo && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-900">
            <strong>Plazo de presentación (60 días):</strong> {plazo}
          </div>
        )}

        {esCopiaDistrito && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 space-y-4">
            <p className="text-sm text-amber-900">
              <strong>Copia de presentación nacional (Art. 53 inc. d, Ley XII-23).</strong> Los
              pasos 2 a 5 se saltean. Cargá los datos SPECA acá y subí el PDF en el paso de{' '}
              <strong>Firma y envío</strong>.
            </p>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
                Código de seguridad SPECA <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                {...register('codigoSpeca')}
                placeholder="Ej: a7f3b9c2e1d84056"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {errors.codigoSpeca && (
                <p className="mt-1 text-xs text-red-600">{errors.codigoSpeca.message}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Candidaturas (solo si ya hay rendición creada) */}
      {rendicion && !esCopiaDistrito && (
        <CandidaturasInline
          rendicionId={rendicion.id}
          readonly={!!snapshotMode || rendicion.estado !== 'BORRADOR'}
          snapshotMode={!!snapshotMode}
        />
      )}

      <div className="flex justify-end gap-3">
        <button
          type="submit"
          disabled={saveMutation.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
        >
          {saveMutation.isPending && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          )}
          {saveMutation.isPending
            ? 'Guardando...'
            : !rendicion
            ? 'Crear rendición →'
            : isDirty
            ? 'Guardar y continuar →'
            : 'Continuar →'}
        </button>
      </div>
    </form>
  );
}

// =============================================================================
// Bloque de Candidaturas (CRUD inline)
// =============================================================================

function CandidaturasInline({
  rendicionId,
  readonly,
  snapshotMode,
}: {
  rendicionId: string;
  readonly: boolean;
  snapshotMode?: boolean;
}) {
  const queryClient = useQueryClient();
  const [categoria, setCategoria] = useState('');
  const [detalle, setDetalle] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['candidaturas-campana', rendicionId],
    queryFn: async () => {
      const res = await rendicionCampanaService.listCandidaturas(rendicionId);
      return (res.data ?? []) as CandidaturaCampana[];
    },
    enabled: !snapshotMode,
    staleTime: snapshotMode ? Infinity : 0,
  });

  const createM = useMutation({
    mutationFn: async () =>
      rendicionCampanaService.createCandidatura(rendicionId, {
        categoria: categoria.trim(),
        detalle: detalle.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Categoría agregada');
      setCategoria('');
      setDetalle('');
      queryClient.invalidateQueries({ queryKey: ['candidaturas-campana', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendicion-campana-validaciones', rendicionId] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? 'No se pudo agregar'),
  });

  const deleteM = useMutation({
    mutationFn: async (id: string) =>
      rendicionCampanaService.deleteCandidatura(rendicionId, id),
    onSuccess: () => {
      toast.success('Categoría eliminada');
      queryClient.invalidateQueries({ queryKey: ['candidaturas-campana', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendicion-campana-validaciones', rendicionId] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? 'No se pudo eliminar'),
  });

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <h3 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2">
        Categorías electorales en las que participó
      </h3>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-xs text-blue-900">
        Cargá cada categoría (ej: Diputados Provinciales) y opcionalmente un detalle (ej: 3
        titulares + 3 suplentes — Circunscripción Sur). A futuro se precargarán del módulo de
        candidaturas.
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Cargando...</p>
      ) : !data || data.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Aún no agregaste ninguna categoría.</p>
      ) : (
        <div className="space-y-2">
          {data.map((c) => (
            <div
              key={c.id}
              className="flex items-start justify-between gap-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
            >
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">{c.categoria}</div>
                {c.detalle && (
                  <div className="text-xs text-gray-600 mt-0.5">{c.detalle}</div>
                )}
              </div>
              {!readonly && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`¿Eliminar la categoría "${c.categoria}"?`)) deleteM.mutate(c.id);
                  }}
                  className="text-red-600 hover:text-red-800 text-xs font-medium flex-shrink-0"
                >
                  Eliminar
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readonly && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-gray-200">
          <input
            type="text"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            placeholder="Categoría (ej: Diputados Provinciales)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 md:col-span-1"
          />
          <input
            type="text"
            value={detalle}
            onChange={(e) => setDetalle(e.target.value)}
            placeholder="Detalle (opcional)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 md:col-span-1"
          />
          <button
            type="button"
            disabled={createM.isPending || categoria.trim().length === 0}
            onClick={() => createM.mutate()}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
          >
            + Agregar categoría
          </button>
        </div>
      )}
    </div>
  );
}
